import { useThemeStore, type ThemePreference } from '../../state/theme.ts';
import { t } from '../../i18n/he.ts';

/**
 * Three segments rather than a two-state switch: "follow the system" has to be
 * reachable, otherwise picking light once permanently opts the device out of
 * its own sunset switch with no way back short of clearing storage.
 */
const OPTIONS: Array<{ value: ThemePreference; label: string; icon: React.ReactNode }> = [
  {
    value: 'light',
    label: t.theme.light,
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 3v1.5m0 15V21m9-9h-1.5m-15 0H3m15.36-6.36l-1.06 1.06M6.7 17.3l-1.06 1.06m12.72 0l-1.06-1.06M6.7 6.7L5.64 5.64M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z"
      />
    ),
  },
  {
    value: 'dark',
    label: t.theme.dark,
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"
      />
    ),
  },
  {
    value: 'system',
    label: t.theme.system,
    icon: (
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.75 17.25h4.5M12 17.25V21m-7.5-3.75h15a1.5 1.5 0 001.5-1.5v-9a1.5 1.5 0 00-1.5-1.5h-15a1.5 1.5 0 00-1.5 1.5v9a1.5 1.5 0 001.5 1.5z"
      />
    ),
  },
];

/**
 * The header is dark in both themes, so this control keeps one palette and does
 * not carry `dark:` variants of its own.
 */
export function ThemeToggle() {
  const preference = useThemeStore((state) => state.preference);
  const setPreference = useThemeStore((state) => state.setPreference);

  return (
    <div
      role="group"
      aria-label={t.theme.label}
      className="flex shrink-0 gap-0.5 rounded-lg border border-slate-700/60 bg-slate-800/80 p-0.5 shadow-inner"
    >
      {OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => setPreference(option.value)}
          title={option.label}
          aria-label={option.label}
          aria-pressed={preference === option.value}
          className={`rounded-md p-1 transition-all ${
            preference === option.value
              ? 'bg-indigo-600 text-white shadow-xs ring-1 ring-indigo-400/30'
              : 'text-slate-400 hover:bg-slate-700/70 hover:text-white'
          }`}
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
            {option.icon}
          </svg>
        </button>
      ))}
    </div>
  );
}
